import type { Membership, Role } from '@hostesa/api-client';

/**
 * Operator-membership resolution for the backoffice.
 *
 * Unlike the platform (which is `:tenantSlug/:hotelSlug` scoped), the backoffice
 * is OPERATOR-level: a Hostesa employee acts across tenants. Only an INTERNAL
 * (Hostesa-staff) membership may enter the backoffice — `hostesa_admin`,
 * `hostesa_support`, or `hostesa_readonly`. A hotel_admin / hotel_staff
 * membership grants NO backoffice access, even though the same person might hold
 * one elsewhere.
 *
 * SECURITY MODEL:
 * - Entry requires one of the internal operator roles below.
 * - What each can DO is gated per-route by permission (OperatorRoute →
 *   useAuth().can()): cross-tenant reads need `admin:cross_tenant_read`
 *   (all three roles); impersonation needs `admin:impersonate` (admin + support
 *   only); provisioning needs `admin:provision` (admin only).
 * - These checks are UX-only affordances; the backend is the authoritative
 *   boundary and every 401/403 is handled identically.
 */

/** The internal/Hostesa-staff roles that may enter the operator console. */
const OPERATOR_ROLES: ReadonlySet<Role> = new Set<Role>([
  'hostesa_admin',
  'hostesa_support',
  'hostesa_readonly',
]);

/** Finds the first internal-operator membership, or null if the user has none. */
export function findOperatorMembership(memberships: readonly Membership[]): Membership | null {
  return memberships.find((m) => OPERATOR_ROLES.has(m.role)) ?? null;
}

/** True when the user holds an internal-operator membership (operator session). */
export function isOperator(memberships: readonly Membership[]): boolean {
  return findOperatorMembership(memberships) !== null;
}
